import { DonateFormSearchValues } from "@components/donate/DonateV3/useDonateFormSearchParams";
import { useGateValue } from "@statsig/react-bindings";

import {
  DISALLOWED_FUNDRAISER_NONPROFIT_IDS,
  GIFT_CARDS_SLUG,
  SPECIAL_FUNDRAISER_NONPROFIT_ID,
} from "@every.org/common/src/entity/constants";
import {
  DonationFlowPaymentOption,
  NominalDonationFlowPaymentOption,
} from "@every.org/common/src/entity/types";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";

const AllDonationFlowPaymentOption = new Set(
  Object.values({
    ...DonationFlowPaymentOption,
    ...NominalDonationFlowPaymentOption,
  })
);

function supportMethodsFromString(string: string) {
  return string
    .split(",")
    .filter(
      (
        paymentMethod
      ): paymentMethod is
        | DonationFlowPaymentOption
        | NominalDonationFlowPaymentOption =>
        AllDonationFlowPaymentOption.has(
          paymentMethod as
            | DonationFlowPaymentOption
            | NominalDonationFlowPaymentOption
        )
    );
}

interface GridCardsDisplayControllerArgs {
  nonprofit: ContextNonprofit;
  initialValuesFromSearchParams: DonateFormSearchValues;
}

export function useAllowCreateFundraiser(nonprofit: ContextNonprofit): boolean {
  const suspectTrafficGate = useGateValue("suspect_traffic");
  if (
    suspectTrafficGate &&
    nonprofit.metadata?.hideFundraiseButtonSuspectTraffic
  ) {
    return false;
  }
  if (DISALLOWED_FUNDRAISER_NONPROFIT_IDS.includes(nonprofit.id)) {
    return false;
  }
  if (
    nonprofit.metadata?.hideFundraiseButton &&
    !nonprofit.metadata?.allowFundraiseLink
  ) {
    return false;
  }
  return true;
}

export function useShowFundraiserLink(nonprofit: ContextNonprofit): boolean {
  const suspectTrafficGate = useGateValue("suspect_traffic");
  if (
    suspectTrafficGate &&
    nonprofit.metadata?.hideFundraiseButtonSuspectTraffic
  ) {
    return false;
  }
  if (DISALLOWED_FUNDRAISER_NONPROFIT_IDS.includes(nonprofit.id)) {
    return false;
  }
  if (nonprofit.id === SPECIAL_FUNDRAISER_NONPROFIT_ID) {
    return false;
  }
  if (nonprofit.metadata?.hideFundraiseButton) {
    return false;
  }
  // Hide fundraise button for unclaimed profiles for compliance
  if (!nonprofit.hasAdmin) {
    return false;
  }

  return true;
}

export function useGridCardsDisplayController({
  nonprofit,
  initialValuesFromSearchParams,
}: GridCardsDisplayControllerArgs) {
  const { method: paymentFlowOptionsFromUrl } = initialValuesFromSearchParams;
  const methods = paymentFlowOptionsFromUrl
    ? supportMethodsFromString(paymentFlowOptionsFromUrl)
    : undefined;

  const showGiftCardLink =
    nonprofit.primarySlug === GIFT_CARDS_SLUG
      ? false
      : methods
      ? methods.includes(DonationFlowPaymentOption.GIFT_CARD)
      : true;

  let showFundraiserLink = useShowFundraiserLink(nonprofit);

  if (
    methods?.includes(NominalDonationFlowPaymentOption.FUNDRAISER) &&
    nonprofit.hasAdmin
  ) {
    showFundraiserLink = true;
  }

  return {
    showGiftCardLink,
    showFundraiserLink,
  };
}
